/* One resolver for explicitly linked products and native Fulfillment variants. */
(function (root) {
  'use strict';
  const normalize = (value) => String(value ?? '').trim().toLowerCase();
  // Ajax carts expose native bundle parents without their components. A fresh
  // Liquid cart supplies per-bundle recipes; quantities always come from Ajax.
  function hydrateCart(cart, seed) {
    let invalid = false;
    const hydrate = (items) => (items || []).map((item) => {
      if (item.item_components?.length) return { ...item, item_components: hydrate(item.item_components) };
      const candidates = (seed?.items || []).filter((candidate) => String(candidate.variant_id ?? candidate.id) === String(item.variant_id ?? item.id) && candidate.bundle_components != null);
      const recipe = item.bundle_components ?? candidates[0]?.bundle_components;
      if (!item.has_components && recipe == null) return item;
      if (candidates.some((candidate) => JSON.stringify(candidate.bundle_components) !== JSON.stringify(recipe)) || !Array.isArray(recipe) || !recipe.length || !Number.isSafeInteger(item.quantity) || item.quantity < 1) {
        invalid = true;
        return item;
      }
      const seen = new Set();
      const components = recipe.map((component) => {
        const id = String(component.variant_id ?? '');
        const quantity = item.quantity * component.quantity;
        if (!/^\d+$/.test(id) || seen.has(id) || !Number.isSafeInteger(component.quantity) || component.quantity < 1 || !Number.isSafeInteger(quantity)) invalid = true;
        seen.add(id);
        return { variant_id: id, quantity };
      });
      return { ...item, item_components: components };
    });
    const items = hydrate(cart?.items);
    return invalid ? null : { ...cart, items };
  }
  function count(cart, id) {
    const seen = new Set();
    const visit = (items) => (items || []).reduce((total, item) => {
      if (item.key && seen.has(item.key)) return total;
      if (item.key) seen.add(item.key);
      const units = String(item.variant_id ?? item.id) === String(id) ? Number(item.quantity) || 0 : 0;
      return total + units + visit(item.item_components);
    }, 0);
    return visit(cart?.items);
  }
  function physicalOptions(variant) {
    const result = {};
    (variant.optionNames || []).forEach((name, index) => {
      if (normalize(name) !== 'fulfillment') result[normalize(name)] = normalize(variant.options[index]);
    });
    return result;
  }
  function matches(variant, selected) {
    const options = physicalOptions(variant);
    const wanted = Object.fromEntries(Object.entries(selected).map(([key, value]) => [normalize(key), normalize(value)]));
    return Object.keys(options).length === Object.keys(wanted).length && Object.entries(options).every(([key, value]) => wanted[key] === value);
  }
  function quantityAllowed(variant, quantity, cart) {
    const total = quantity + count(cart, variant.id);
    const rule = variant.quantityRule || {};
    return total >= (rule.min || 1) && (!rule.max || total <= rule.max) && total % (rule.increment || 1) === 0;
  }
  function productionWeeks(variant) {
    const min = variant?.mtoMinWeeks;
    const max = variant?.mtoMaxWeeks;
    const empty = (value) => value === null || value === undefined || value === '';
    if (empty(min) && empty(max)) return 8;
    return Number.isSafeInteger(min) && min > 0 && Number.isSafeInteger(max) && max >= min ? max : null;
  }
  function purchaseProperties(result, properties = {}) {
    const next = { ...properties, Fulfillment: result.state === 'mto' ? 'Made to Order' : 'Ready to Ship', 'Shipping time': result.lead,
      _wolf_fulfillment_routing: 'automatic' };
    delete next._wolf_mto_weeks;
    if (result.state === 'mto' && result.leadWeeks) next._wolf_mto_weeks = String(result.leadWeeks);
    return next;
  }
  function componentRoute(variant, quantity, cart) {
    if (variant?.components == null) return { eligible: true, remaining: null, inCart: 0, leadWeeks: 0 };
    if (!Array.isArray(variant.components) || !variant.components.length) return { error: true };
    const seen = new Set();
    let remaining = Infinity;
    let inCart = 0;
    let leadWeeks = 0;
    let eligible = true;
    for (const component of variant.components) {
      if (component.configurationError || !Number.isSafeInteger(component.quantity) || component.quantity < 1 || seen.has(String(component.variantId))) return { error: true };
      seen.add(String(component.variantId));
      const matches = (component.variants || []).filter((candidate) => String(candidate.id) === String(component.variantId));
      if (matches.length !== 1 || matches[0].kind !== variant.kind || matches[0].components != null) return { error: true };
      const requested = quantity * component.quantity;
      if (!Number.isSafeInteger(requested)) return { error: true };
      const result = resolve(component, physicalOptions(matches[0]), requested, cart, { keepMto: variant.kind === 'mto' });
      if (['configuration', 'inventory-configuration'].includes(result.reason)) return { error: true };
      eligible = eligible && result.purchasable && result.state === variant.kind;
      remaining = Math.min(remaining, Math.floor((result.remaining || 0) / component.quantity));
      inCart = Math.max(inCart, result.inCart || 0);
      leadWeeks = Math.max(leadWeeks, result.leadWeeks || 0);
    }
    return { eligible, remaining, inCart, leadWeeks };
  }
  function resolve(config, selected, quantity, cart = config.cart, { keepMto = false } = {}) {
    const unavailable = (reason, extra = {}) => ({ state: 'unavailable', reason, purchasable: false, ...extra });
    if (config.configurationError) return unavailable('configuration');
    if (!Number.isSafeInteger(quantity) || quantity < 1) return unavailable('quantity');
    cart = hydrateCart(cart, config.cart);
    if (!cart) return unavailable('configuration');
    const candidates = (config.variants || []).filter((variant) => matches(variant, selected));
    const rtsCandidates = candidates.filter((variant) => variant.kind === 'rts');
    const mtoCandidates = candidates.filter((variant) => variant.kind === 'mto');
    if (rtsCandidates.length > 1 || mtoCandidates.length > 1 || candidates.some((variant) => variant.kind === 'invalid')) return unavailable('configuration');
    const rts = rtsCandidates[0];
    const mto = mtoCandidates[0];
    if (config.pairedBundle && candidates.some((variant) => variant.components == null)) return unavailable('configuration');
    const mode = config.nativeFulfillment ? (mto?.sellingMode || 'RTS + MTO') : null;
    if (mode && !['RTS + MTO', 'MTO only', 'RTS only'].includes(mode)) return unavailable('configuration');
    const leadWeeks = config.nativeFulfillment && mto ? productionWeeks(mto) : null;
    if (config.nativeFulfillment && mto && leadWeeks === null) return unavailable('configuration');
    // Canonical physical options own seasonal visibility. An MTO twin cannot reopen a hidden color.
    const canonical = candidates.filter((variant) => String(variant.productId) === String(config.productId));
    if (!canonical.length || canonical.every((variant) => variant.hidden) || rts?.hidden) return unavailable('hidden');
    const override = config.nativeFulfillment ? (mode === 'MTO only' ? 'mto' : 'automatic') : (rts?.override || mto?.override || config.override || 'automatic');
    if (!['automatic', 'mto', 'unavailable'].includes(override)) return unavailable('configuration');
    if (override === 'unavailable') return unavailable('disabled', { canonicalVariant: rts || mto });
    // Available is not the same as physically stocked: never use untracked or oversellable RTS stock.
    const rtsComponents = componentRoute(rts, quantity, cart);
    const mtoComponents = componentRoute(mto, quantity, cart);
    if (rtsComponents.error || mtoComponents.error) return unavailable('configuration');
    const reliable = rts && (rts.components != null || rts.inventoryManagement === 'shopify' && rts.inventoryPolicy === 'deny' && Number.isInteger(rts.inventoryQuantity));
    if (rts && !reliable && mode !== 'MTO only') return unavailable('inventory-configuration', { canonicalVariant: rts });
    const parentInCart = rts ? count(cart, rts.id) : 0;
    const inCart = Math.max(parentInCart, rtsComponents.inCart || 0);
    const remaining = reliable && rts.available ? (rts.components != null ? rtsComponents.remaining : Math.max(0, rts.inventoryQuantity - parentInCart)) : 0;
    if (!keepMto && override !== 'mto' && rts && rts.available && rtsComponents.eligible && quantity <= remaining && quantityAllowed(rts, quantity, cart)) {
      return { state: 'rts', purchasable: true, variant: rts, canonicalVariant: rts, remaining, inCart, lead: config.nativeFulfillment ? '3–5 business days' : config.rtsLead };
    }
    const permission = config.nativeFulfillment ? mode !== 'RTS only' : config.mtoAllowed === true && rts?.mtoAllowed !== false && mto?.mtoAllowed !== false;
    const mtoStock = mto && (mto.components != null ? mtoComponents.eligible : !mto.inventoryManagement || mto.inventoryPolicy === 'continue' || mto.inventoryQuantity - count(cart, mto.id) >= quantity);
    if (permission && mto && !mto.hidden && mto.available && mtoStock && quantityAllowed(mto, quantity, cart)) {
      const parentHasTiming = mto.mtoMinWeeks != null && mto.mtoMinWeeks !== '' || mto.mtoMaxWeeks != null && mto.mtoMaxWeeks !== '';
      const parentWeeks = mto.components != null && !parentHasTiming ? 0 : leadWeeks;
      const maximumWeeks = Math.max(parentWeeks || 0, mtoComponents.leadWeeks || 0) || null;
      return { state: 'mto', purchasable: true, variant: mto, canonicalVariant: rts || mto, remaining, inCart,
        lead: config.nativeFulfillment ? `Up to ${maximumWeeks} weeks` : config.mtoLead, leadWeeks: maximumWeeks };
    }
    if (rts && remaining >= quantity && !quantityAllowed(rts, quantity, cart)) return unavailable('quantity-rule', { canonicalVariant: rts, rule: rts.quantityRule });
    return unavailable(override === 'mto' ? 'mto-unavailable' : (remaining > 0 ? 'insufficient' : 'sold-out'), { remaining, inCart, canonicalVariant: rts });
  }
  function message(result) {
    if (result.state === 'rts') return result.remaining <= 5 ? `${result.remaining} in stock` : 'In Stock';
    if (result.state === 'mto') return 'Made to order';
    if (result.reason === 'quantity-rule') {
      const rule = result.rule || {};
      return `Please choose a quantity that meets this option’s requirements: minimum ${rule.min || 1}${rule.max ? `, maximum ${rule.max}` : ''}${rule.increment > 1 ? `, multiples of ${rule.increment}` : ''}, including any already in your bag.`;
    }
    if (result.reason === 'quantity') return 'Please enter a whole quantity of at least 1.';
    if (result.reason === 'insufficient') return result.remaining <= 5 ? `Only ${result.remaining} available. Please reduce your quantity.` : 'This quantity isn’t available. Please reduce your quantity.';
    if (['configuration', 'inventory-configuration'].includes(result.reason)) return 'This option is temporarily unavailable. Please contact us for help.';
    return 'This option is currently unavailable.';
  }
  const api = { resolve, message, count, physicalOptions, matches, productionWeeks, purchaseProperties, hydrateCart };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.WolfPurchasing = api;
})(typeof window === 'undefined' ? globalThis : window);
